import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useLanguageLink } from '../../hooks/useLanguageLink';
import SEO from '../../components/seo/SEO';

export default function PrivacyPolicyPage() {
  const { t } = useTranslation('privacy');
  const { createLink } = useLanguageLink();

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <SEO
        title={t('seo.title')}
        description={t('seo.description')}
        canonical="/privacy"
        noindex={true}
      />
      <div className="py-12 bg-gradient-to-br from-primary-50 via-white to-purple-50">
        <div className="container-marketing max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-purple-600">
              {t('title')}
            </span>
          </h1>
          <p className="text-gray-600">{t('last_updated')}</p>
        </div>
      </div>

      <div className="py-12">
        <div className="container-marketing max-w-4xl prose prose-lg max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('introduction.title')}</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('introduction.paragraph1')}
            </p>
            <p className="text-gray-700 leading-relaxed">
              {t('introduction.paragraph2')}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('information_collection.title')}</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('information_collection.personal_info.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('information_collection.personal_info.intro')}
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              {t('information_collection.personal_info.items', { returnObjects: true }).map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('information_collection.usage_data.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('information_collection.usage_data.intro')}
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              {t('information_collection.usage_data.items', { returnObjects: true }).map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('data_usage.title')}</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('data_usage.intro')}
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              {t('data_usage.items', { returnObjects: true }).map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('data_retention.title')}</h2>
            <p className="text-gray-700 leading-relaxed">
              {t('data_retention.content')}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('data_security.title')}</h2>
            <p className="text-gray-700 leading-relaxed">
              {t('data_security.intro')}
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              {t('data_security.items', { returnObjects: true }).map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('gdpr.title')}</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('gdpr.intro')}
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              {t('gdpr.items', { returnObjects: true }).map((item: any, index: number) => (
                <li key={index}><strong>{item.title}:</strong> {item.description}</li>
              ))}
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('third_party.title')}</h2>
            <p className="text-gray-700 leading-relaxed">
              {t('third_party.content')}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('cookies.title')}</h2>
            <p className="text-gray-700 leading-relaxed">
              {t('cookies.content')} <Link to={createLink('cookies')} className="text-primary-600 hover:text-primary-700 font-semibold">{t('cookies.cookie_policy_link')}</Link> {t('cookies.content_end')}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('changes.title')}</h2>
            <p className="text-gray-700 leading-relaxed">
              {t('changes.content')}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('contact.title')}</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('contact.intro')}
            </p>
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <p className="text-gray-700"><strong>{t('contact.email_label')}:</strong> {t('contact.email')}</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
